// Single API client. When adding endpoints, add the method here — never raw fetch in components.
// Type the generic at the call site: request<Client[]>('/clients').

const API_BASE = '/api';

/**
 * A refusal that kept its reasons. Some endpoints answer "no" with structure —
 * an unlink 409 carries `cited_by: [{id, ref}]` — and the UI should render that,
 * not just the flattened message. Plain `instanceof Error` handling still works.
 */
export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;
  constructor(status: number, body: Record<string, unknown>, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    const body = (error ?? {}) as Record<string, unknown>;
    throw new ApiError(
      response.status,
      body,
      (body as { error?: string }).error || `Request failed (${response.status})`,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/** Multipart upload helper (multer routes) — no JSON content-type. */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, { method: 'POST', credentials: 'include', body: form });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error((error as { error?: string }).error || `Upload failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export const api = {
  request,
  upload,

  // ---- Clients (CRM slice) ----
  getClients: () => request<any[]>('/clients'),

  // ---- Engagements (lifecycle slice) ----
  getEngagements: () => request<any[]>('/engagements'),

  // Sections below are appended by their slices:
  // ---- Documents ----
  // ---- Register ----
  // ---- Deliverables / ACs / TCs ----
  // ---- Contracting ----
  // ---- Acceptance ----
  // ---- Agent ----
  // ---- Portal admin ----
  // ---- Settings ----
};
